/**
 * Gestionnaire de session — supporte sessionStorage ET localStorage (fallback)
 */

export interface SessionInfo {
  isValid: boolean;
  isAdmin: boolean;
  userId?: string;
  timeRemaining?: number;
}

export function checkSession(): SessionInfo {
  // Lire depuis sessionStorage en priorité, puis localStorage en fallback
  const sessionExpiry =
    sessionStorage.getItem('sessionExpiry') ||
    localStorage.getItem('sessionExpiry');

  const userSessionToken =
    sessionStorage.getItem('userSessionToken') ||
    localStorage.getItem('userSessionToken');

  const adminSessionToken = sessionStorage.getItem('adminSessionToken');

  const sessionUserId =
    sessionStorage.getItem('sessionUserId') ||
    localStorage.getItem('sessionUserId');

  const adminPhone = sessionStorage.getItem('adminPhone');

  // Pas de session du tout
  if (!sessionExpiry || (!userSessionToken && !adminSessionToken)) {
    // Dernier recours : vérifier si currentUser existe dans localStorage
    const currentUser = localStorage.getItem('currentUser');
    if (currentUser) {
      try {
        const user = JSON.parse(currentUser);
        if (user?.phone) {
          // Recréer une session valide
          const newExpiry = Date.now() + 7 * 24 * 60 * 60 * 1000;
          const newToken = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          sessionStorage.setItem('userSessionToken', newToken);
          sessionStorage.setItem('sessionUserId', user.phone);
          sessionStorage.setItem('sessionExpiry', newExpiry.toString());
          localStorage.setItem('userSessionToken', newToken);
          localStorage.setItem('sessionUserId', user.phone);
          localStorage.setItem('sessionExpiry', newExpiry.toString());
          return {
            isValid: true,
            isAdmin: false,
            userId: user.phone,
            timeRemaining: newExpiry - Date.now(),
          };
        }
      } catch { /* ignore */ }
    }
    return { isValid: false, isAdmin: false };
  }

  const expiryTime = parseInt(sessionExpiry, 10);
  const now = Date.now();

  if (now > expiryTime) {
    clearSession();
    return { isValid: false, isAdmin: false };
  }

  const timeRemaining = expiryTime - now;
  const isAdmin = !!adminSessionToken && !!adminPhone;
  const userId = isAdmin ? adminPhone : sessionUserId;

  return {
    isValid: true,
    isAdmin,
    userId: userId || undefined,
    timeRemaining,
  };
}

export function clearSession(): void {
  sessionStorage.removeItem('userSessionToken');
  sessionStorage.removeItem('adminSessionToken');
  sessionStorage.removeItem('sessionExpiry');
  sessionStorage.removeItem('sessionUserId');
  sessionStorage.removeItem('adminPhone');
  localStorage.removeItem('userSessionToken');
  localStorage.removeItem('sessionExpiry');
  localStorage.removeItem('sessionUserId');
}

export function renewSession(): void {
  const session = checkSession();
  if (session.isValid) {
    const newExpiry = Date.now() + 7 * 24 * 60 * 60 * 1000;
    sessionStorage.setItem('sessionExpiry', newExpiry.toString());
    localStorage.setItem('sessionExpiry', newExpiry.toString());
  }
}

export function formatTimeRemaining(milliseconds: number): string {
  const hours = Math.floor(milliseconds / (1000 * 60 * 60));
  const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) return `${hours}h ${minutes}min`;
  return `${minutes}min`;
}

export function startSessionMonitoring(onExpired: () => void): () => void {
  const interval = setInterval(() => {
    const session = checkSession();
    if (!session.isValid) onExpired();
  }, 60000);
  return () => clearInterval(interval);
}
